// Client-side rules for attaching a reference document. The canonical MIME type is derived from the
// filename extension rather than trusted from the browser's `File.type`, which is empty or
// inconsistent for several of our supported types (notably .md and .csv) — and the agentic-workflow
// validator checks against exactly this MIME set, so sending a browser-guessed type would get a
// legitimate .md rejected server-side.

export const NOVA_FILE_MAX_SIZE_BYTES = 10 * 1024 * 1024; // 10 MB

const MIME_BY_EXTENSION: Record<string, string> = {
	pdf: 'application/pdf',
	docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
	txt: 'text/plain',
	md: 'text/markdown',
	csv: 'text/csv',
	xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
};

// The extensions the file picker advertises and the drop zone accepts.
export const NOVA_FILE_ACCEPTED_EXTENSIONS = Object.keys(MIME_BY_EXTENSION).map(extension => `.${extension}`);

function extensionOf(filename: string): string {
	const dot = filename.lastIndexOf('.');
	return dot >= 0 ? filename.slice(dot + 1).toLowerCase() : '';
}

// The canonical MIME to send for a supported file, or null if the extension isn't one we accept.
export function canonicalMimeType(filename: string): string | null {
	return MIME_BY_EXTENSION[extensionOf(filename)] ?? null;
}

// The short, uppercase type badge shown against an attached file ("PDF", "DOCX"). Derived from the
// extension for the same reason the MIME is (see above): it is the one thing about a file that is
// always present and consistent. Falls back to a generic label rather than an empty badge, so a
// record whose filename somehow has no extension still renders a well-formed row.
export function fileTypeLabel(filename: string): string {
	const extension = extensionOf(filename);
	return extension ? extension.toUpperCase() : 'FILE';
}

export type NovaFileRejectionReason = 'type' | 'size';

export interface NovaFileValidation {
	ok: boolean;
	reason?: NovaFileRejectionReason;
}

export function validateNovaFile(file: {name: string; size: number}): NovaFileValidation {
	if (!canonicalMimeType(file.name)) {
		return {ok: false, reason: 'type'};
	}
	if (file.size <= 0 || file.size > NOVA_FILE_MAX_SIZE_BYTES) {
		return {ok: false, reason: 'size'};
	}
	return {ok: true};
}
